import React from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '../../../assets/icons/delete-icon.svg';
import SearchIcon from '../../../assets/icons/search-icon.svg';
import errorImage from '../../../assets/images/empty/errorPerson.png';
import LoadingSpinner from '../../loadingSpinner/loadingSpinner';
import CustomTablePagination from '../../customTablePagination/customTablePagination';
import { TableColumnInterface } from '../../../interfaces/tableColumn.interface';

interface TokensTableProps {
  title: string;
  columns: TableColumnInterface[];
  rows: any[];
  total: number;
  page: number;
  rowsPerPage: number;
  setPage: (_newPage: number) => void;
  setRowsPerPage: (_rowsPerPage: number) => void;
  isFetching: boolean;
  isError: boolean;
  isDeleting?: boolean;
  refetch: () => void;
  search: string;
  setSearch: (_search: string) => void;
  onDelete?: (_item: any) => void;
  headerContent?: React.ReactNode;
}

function safeValue(value: any) {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return '[unrenderable]';
    }
  }
  return String(value);
}

function capitalizeLabel(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const TokensTable: React.FC<TokensTableProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 rounded-[8px] border-[1px] bg-white">
      <div className="flex items-center justify-between gap-3 border-b-[1px] px-4 py-2.5">
        <div className="text-sm min-w-[100px] font-medium text-primary">{capitalizeLabel((t(props.title) || props.title) as string)}</div>
        <div className="flex w-full items-center justify-end gap-2">
          {props.headerContent}
          <div className="flex h-[42px] w-full max-w-[353px] items-center gap-2 rounded-[8px] border-[1px] bg-white px-4">
            <img src={SearchIcon} alt="" />
            <input
              value={props.search}
              onChange={(e) => props.setSearch(e.target.value)}
              placeholder={t('search') as string}
              className="w-full bg-transparent text-[14px] text-primary outline-none"
            />
          </div>
        </div>
      </div>

      <div>
        {props.isError && (
          <div className="flex min-h-[400px] items-center justify-center px-3 py-2 text-primary">
            <div className="grid place-items-center text-primary">
              <img className="mb-4 max-h-[240px] max-w-[240px]" src={errorImage} alt="Error" />
              <p>{t('wereSorryIssue')}</p>
              <button
                type="button"
                onClick={() => props.refetch()}
                className="text-[#4C956C]"
              >
                {t('tryToRefreshLatest')}
              </button>
            </div>
          </div>
        )}

        {props.isFetching && !props.isError ? (
          <div className="flex h-full min-h-[400px] items-center justify-center">
            <LoadingSpinner color="primary" size={50} center />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="border-b-[1px] bg-[#F5F5F5]">
                <tr className="text-sm font-medium text-primary">
                  {props.columns.map((c) => (
                    <th
                      key={c.field}
                      className="whitespace-nowrap border-r border-[#EAEAEA] px-4 py-3 text-left"
                    >
                      {capitalizeLabel((t(c.headerName) || c.headerName) as string)}
                    </th>
                  ))}
                  {props.onDelete && (
                    <th className="whitespace-nowrap px-4 py-3 text-left">
                      {capitalizeLabel((t('actions') || 'Actions') as string)}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {props.rows.map((row, idx) => (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${row?.tokenType ?? ''}-${row?.id ?? idx}`}
                    className="border-b-[1px] text-sm text-primary hover:bg-[#FAFAFA]"
                  >
                    {props.columns.map((c) => {
                      const cellValue = row?.[c.field];

                      return (
                        <td key={c.field} className="max-w-[260px] truncate border-r border-[#EAEAEA] px-4 py-3">
                          {c.cellRenderer
                            ? c.cellRenderer(cellValue, row)
                            : safeValue(cellValue)}
                        </td>
                      );
                    })}
                    {props.onDelete && (
                      <td className="p-4 py-3">
                        <button
                          type="button"
                          onClick={() => props.onDelete?.(row)}
                          disabled={props.isDeleting}
                          className="rounded-md p-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <img className="h-[20px] w-[20px]" src={DeleteIcon} alt="" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!props.rows.length && !props.isError && !props.isFetching && (
              <div className="flex min-h-[200px] items-center justify-center px-3 py-8 text-sm text-primary">
                {t('noData') || 'No data'}
              </div>
            )}
          </div>
        )}
      </div>

      {!props.isFetching && (
        <CustomTablePagination
          count={props.total}
          page={props.page}
          rowsPerPage={props.rowsPerPage}
          setPage={props.setPage}
          refetch={props.refetch}
          setRowsPerPage={props.setRowsPerPage}
        />
      )}
    </div>
  );
};

export default TokensTable;
